import React, { useEffect, useState } from "react";
import { MicRow, appendText } from "@/components/VoiceMic";
import { vendorReviews, postVendorReview, reviewEligibility, replyVendorReview } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Star, MessageSquarePlus, Loader2, CornerDownRight, Edit3 } from "lucide-react";
import { toast } from "sonner";

function timeAgo(iso) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 2592000) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
}

const shopApi = (slug) => ({
  list: () => vendorReviews(slug),
  post: (payload) => postVendorReview(slug, payload),
  eligibility: () => reviewEligibility(slug),
  reply: (reviewId, text) => replyVendorReview(slug, reviewId, text),
});

// `api` lets services reuse this UI; defaults to shop reviews by slug.
export default function ReviewsSection({ slug, api: apiProp, isOwner = false, shopName = "", selfText = "Aap apni khud ki shop ka review nahi de sakte.", testid = "vendor-reviews-section", onRatingChanged }) {
  const { user } = useApp();
  const api = apiProp || shopApi(slug);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eligible, setEligible] = useState(false);
  const [eligKnown, setEligKnown] = useState(false);
  const [myReview, setMyReview] = useState(null);
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const list = await api.list();
      setReviews(list);
      if (user) {
        try {
          const e = await api.eligibility();
          setEligible(!!e.eligible);
          setEligKnown(true);
          setMyReview(e.my_review || null);
          if (e.my_review) {
            setRating(e.my_review.rating);
            setComment(e.my_review.comment || "");
          }
        } catch { /* eligibility unknown: review form stays hidden, list still shows */ }
      } else {
        setEligible(false);
        setMyReview(null);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug, apiProp ? testid : "", user?.id]);
  // Deep link from a review alert (#<testid>) scrolls here once loaded.
  useEffect(() => {
    if (!loading && window.location.hash === `#${testid}`) setTimeout(() => document.getElementById(testid)?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
  }, [loading, testid]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post({ rating, comment });
      toast.success(myReview ? "Review updated" : "Thanks for your review!");
      setOpen(false);
      onRatingChanged?.(res.rating, res.reviews_count);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not post review");
    } finally { setSaving(false); }
  };

  const handleReplied = (reviewId, replyObj) => {
    setReviews((prev) => prev.map((r) => (r.id === reviewId ? { ...r, vendor_reply: replyObj } : r)));
  };

  return (
    <section className="mt-10 scroll-mt-28" id={testid} data-testid={testid}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2">
          <Star className="w-5 h-5 text-amber-500 fill-amber-500" /> Reviews ({reviews.length})
        </h2>
        {user && eligible && (
          <button onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 text-sm font-semibold" data-testid="write-review-btn">
            <MessageSquarePlus className="w-4 h-4" /> {myReview ? "Edit your review" : "Write a review"}
          </button>
        )}
      </div>

      {!user && (
        <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-sm text-amber-900 mb-4" data-testid="review-login-hint">
          Review aur rating dene ke liye login karein.
        </div>
      )}
      {user && eligKnown && !eligible && !myReview && !isOwner && (
        <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 text-sm text-slate-600 mb-4" data-testid="review-not-eligible">
          {selfText}
        </div>
      )}

      {open && (
        <form onSubmit={submit} className="bg-white border border-slate-100 rounded-2xl p-4 sm:p-5 mb-4" data-testid="review-form">
          <div className="flex items-center gap-1 mb-3">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" onClick={() => setRating(n)} aria-label={`${n} star`} data-testid={`star-${n}`}>
                <Star className={`w-7 h-7 ${n <= rating ? "text-amber-500 fill-amber-500" : "text-slate-300"}`} />
              </button>
            ))}
            <span className="text-sm text-slate-500 ml-2">{rating}/5</span>
          </div>
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="Share your experience (optional)" className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500" data-testid="review-comment" />
          <MicRow onText={(t) => setComment((v) => appendText(v, t))} testid="mic-review-comment" />
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">Cancel</button>
            <button type="submit" disabled={saving} className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-5 py-2 text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-1.5" data-testid="submit-review-btn">
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {myReview ? "Update review" : "Post review"}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="py-6 text-center text-slate-500 text-sm">Loading reviews...</div>
      ) : reviews.length === 0 ? (
        <div className="bg-white border border-slate-100 rounded-2xl p-6 text-center text-sm text-slate-500" data-testid="reviews-empty">
          No reviews yet. {user && eligible ? "Be the first!" : ""}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="reviews-list">
          {reviews.map((r, i) => (
            <div key={r.id || i} className="bg-white border border-slate-100 rounded-2xl p-4" data-testid={`review-${i}`}>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <div className="w-8 h-8 rounded-full bg-emerald-50 text-emerald-800 font-bold inline-flex items-center justify-center text-xs">{(r.user_name || "C").slice(0, 1).toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{r.user_name || "Customer"}</div>
                  <div className="text-[10px] text-slate-400">{timeAgo(r.created_at)}</div>
                </div>
                <div className="inline-flex items-center gap-0.5 text-amber-500">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "fill-amber-500" : "text-slate-200"}`} />
                  ))}
                </div>
              </div>
              {r.comment && <p className="text-sm text-slate-700 mt-1 leading-relaxed">{r.comment}</p>}

              {/* Vendor reply (public read) */}
              {r.vendor_reply?.text && (
                <div className="mt-3 ml-2 pl-3 border-l-2 border-emerald-200 bg-emerald-50/40 rounded-r-lg p-2.5" data-testid={`vendor-reply-${i}`}>
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-800">
                    <CornerDownRight className="w-3 h-3" />
                    <span>Reply from {r.vendor_reply.vendor_name || shopName || "Vendor"}</span>
                    <span className="text-emerald-600 font-normal">· {timeAgo(r.vendor_reply.replied_at)}</span>
                  </div>
                  <p className="text-sm text-slate-700 mt-1 leading-relaxed whitespace-pre-wrap">{r.vendor_reply.text}</p>
                </div>
              )}

              {/* Owner-only reply editor */}
              {isOwner && r.id && (
                <OwnerReplyEditor
                  reply={api.reply}
                  reviewId={r.id}
                  existingReply={r.vendor_reply}
                  onSaved={(replyObj) => handleReplied(r.id, replyObj)}
                  index={i}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function OwnerReplyEditor({ reply, reviewId, existingReply, onSaved, index }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(existingReply?.text || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setText(existingReply?.text || ""); }, [existingReply?.text]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) { toast.error("Reply cannot be empty"); return; }
    setSaving(true);
    try {
      const res = await reply(reviewId, text.trim());
      toast.success(existingReply ? "Reply updated" : "Reply posted");
      setOpen(false);
      onSaved?.(res.vendor_reply);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not post reply");
    } finally { setSaving(false); }
  };

  if (!open) {
    return (
      <div className="mt-2">
        <button
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900"
          data-testid={`reply-btn-${index}`}
        >
          {existingReply ? <><Edit3 className="w-3 h-3" /> Edit reply</> : <><CornerDownRight className="w-3 h-3" /> Reply to review</>}
        </button>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="mt-3 bg-emerald-50/40 border border-emerald-100 rounded-xl p-3" data-testid={`reply-form-${index}`}>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        maxLength={1000}
        placeholder="Thank the customer or address their feedback..."
        className="w-full border border-emerald-200 rounded-lg px-2.5 py-2 text-sm outline-none focus:border-emerald-500 bg-white"
        data-testid={`reply-textarea-${index}`}
      />
      <MicRow onText={(t) => setText((v) => appendText(v, t))} testid={`mic-reply-${index}`} />
      <div className="mt-2 flex justify-end gap-2">
        <button type="button" onClick={() => setOpen(false)} className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600">Cancel</button>
        <button type="submit" disabled={saving} className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-1.5 text-xs font-semibold disabled:opacity-60 inline-flex items-center gap-1" data-testid={`reply-submit-${index}`}>
          {saving && <Loader2 className="w-3 h-3 animate-spin" />}
          {existingReply ? "Update reply" : "Post reply"}
        </button>
      </div>
    </form>
  );
}
